/**
 * Medical Review Service - Simple wrapper around existing document system
 */

import { legalDocumentService } from './legalDocumentService';
import { Matter } from '../types';

export interface UploadMedicalDocumentsRequest {
  matter: Matter;
  files: File[];
  caseType?: 'motor_vehicle' | 'medical_malpractice' | 'workers_comp' | 'other';
}

export interface UploadMedicalDocumentsResponse {
  success: boolean;
  uploadedFiles: Array<{
    fileName: string;
    filePath: string;
    fileSize: number;
    documentId: string;
  }>;
  message?: string;
  error?: string;
}

class MedicalReviewService {
  /**
   * Upload medical documents using the existing proven document system
   */
  async uploadDocuments(request: UploadMedicalDocumentsRequest): Promise<UploadMedicalDocumentsResponse> {
    try {
      const uploadedFiles = [];
      
      for (const file of request.files) {
        try {
          // Use the existing document system with medical_records category
          const result = await legalDocumentService.uploadDocument(
            file,
            request.matter,
            'medical_records',
            'confidential',
            request.caseType ? [request.caseType] : [],
            'medical-records' // folder path
          );
          
          if (result.success) {
            uploadedFiles.push({
              fileName: result.document.name,
              filePath: result.document.webUrl || '',
              fileSize: result.document.size,
              documentId: result.document.id
            });
          }
        } catch (fileError) {
          console.error(`Failed to upload file ${file.name}:`, fileError);
          // Continue with other files but log the error
        }
      }

      return {
        success: uploadedFiles.length > 0,
        uploadedFiles,
        message: `Successfully uploaded ${uploadedFiles.length} of ${request.files.length} medical records`
      };
      
    } catch (error: any) {
      console.error('Failed to upload medical documents:', error);
      return {
        success: false,
        uploadedFiles: [],
        error: error.message || 'Failed to upload medical documents'
      };
    }
  }

  /**
   * Get medical documents for a matter (from the medical-records folder)
   * This method is completely separate from Matter Details document display
   */
  async getMedicalDocuments(matter: Matter) {
    try {
      // Get documents specifically from the medical-records folder
      // This does NOT affect the main document listing used by Matter Details
      const medicalDocs = await legalDocumentService.listDocumentsInFolder(matter, 'medical-records');
      
      // Override category to ensure they're properly identified as medical records
      const validMedicalDocs = medicalDocs.map(doc => ({
        ...doc,
        category: 'medical_records' as const
      }));
      
      return { documents: validMedicalDocs, containerCreated: true };
    } catch (error: any) {
      console.error('Failed to get medical documents:', error);
      
      // If folder doesn't exist yet, that's not an error - just no documents
      if (error.message?.includes('folder') || error.message?.includes('not found')) {
        console.log('Medical records folder does not exist yet - no documents uploaded');
        return { documents: [], containerCreated: true };
      }
      
      return { documents: [], containerCreated: false };
    }
  }
}

// Export singleton instance
export const medicalReviewService = new MedicalReviewService();